<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header liga-float-header signup-header">
      <ion-toolbar class="signup-float-toolbar">
        <div class="liga-float-bar signup-float-bar">
          <button type="button" class="liga-float-circle" aria-label="Назад" @click="navigate('login')">
            <ion-icon :icon="arrowBackOutline" />
          </button>
          <div class="liga-float-title-pill">
            <span class="liga-float-title-text">Регистрация</span>
          </div>
          <div class="liga-float-spacer" aria-hidden="true" />
        </div>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background" :fullscreen="true" :scroll-y="true">
      <ion-card class="signup-card bg-transparent">
        <ion-card-content>
          <ion-input type="email" inputmode="email" placeholder="Электронная почта"
            v-model="registerAccount.email" :disabled="loading"></ion-input>
          <ion-input type="text" placeholder="Имя" v-model="registerAccount.firstName" :disabled="loading"></ion-input>
          <ion-input type="text" placeholder="Фамилия" v-model="registerAccount.lastName" :disabled="loading"></ion-input>
          <ion-input type="password" placeholder="Пароль" v-model="registerAccount.password" :disabled="loading"></ion-input>
          <ion-input type="password" placeholder="Пароль еще раз" v-model="confirmPassword" :disabled="loading"></ion-input>
          <div class="main-button-container">
            <ion-button v-on:click="singUp()" class="signup-button" :disabled="loading">
              <p v-show="!loading">Регистрация</p>
              <ion-spinner name="bubbles" v-show="loading"></ion-spinner>
            </ion-button>
          </div>
        </ion-card-content>
      </ion-card>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from '@/model/globalcontext.model';
import router from '@/router/index';
import AccountService from '@/services/account.service';
import { flushPendingPushOpen, registerAdminPushNotifications } from '@/services/push.service';
import { flushPendingDeepLink } from '@/services/deep-link.service';
import { isValidEmail, stripAuthWhitespace } from '@/utils/auth-input';
import { getClientDeviceMeta } from '@/utils/client-device-meta';
import { CapacitorHttp } from '@capacitor/core';
import {
    IonButton,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonPage,
    IonSpinner,
    IonToolbar,
    alertController,
} from '@ionic/vue';
import axios, { isAxiosError } from 'axios';
import { arrowBackOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';

export default defineComponent({
  props: {
    timeout: { type: Number, default: 1000 },
  },
  name: 'SignUp',
  components: {
    IonContent,
    IonPage,
    IonHeader,
    IonToolbar,
    IonCard,
    IonCardContent,
    IonInput,
    IonButton,
    IonIcon,
    IonSpinner,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    const accountService = inject('accountService') as AccountService;
    const isMobile = globalContext.isMobile;

    return {
      arrowBackOutline,
      isMobile,
      accountService,
      globalContext,
    }
  },
  data() {
    return {
      registerAccount: {
        login: '',
        email: '',
        firstName: '',
        lastName: '',
        password: ''
      },
      confirmPassword: '',
      loading: false,
    }
  },

  ionViewWillEnter() {
    this.registerAccount = {
      login: '',
      email: '',
      firstName: '',
      lastName: '',
      password: ''
    };
    this.confirmPassword = '';
    this.loading = false;
  },

  methods: {
    navigate(routeName: string): void {
      router.push(`/${routeName}`);
    },

    problemMessage(data: unknown, fallback: string): string {
      const body = data as { title?: string; detail?: string; message?: string } | null;
      if (body?.title && !body.title.startsWith('error.')) return body.title;
      if (body?.detail && !String(body.detail).startsWith('error.')) return body.detail;
      return fallback;
    },

    async singUp(): Promise<void> {
      if (this.loading) return;

      const email = stripAuthWhitespace(this.registerAccount.email).toLowerCase();
      const password = stripAuthWhitespace(this.registerAccount.password);
      const confirmPassword = stripAuthWhitespace(this.confirmPassword);
      const firstName = this.registerAccount.firstName.trim();
      const lastName = this.registerAccount.lastName.trim();
      this.registerAccount.email = email;
      this.registerAccount.password = password;
      this.confirmPassword = confirmPassword;
      this.registerAccount.firstName = firstName;
      this.registerAccount.lastName = lastName;

      if (!isValidEmail(email)) {
        await this.showAlert('Ошибка', 'Укажите корректный адрес электронной почты');
        return;
      } else if (firstName.length < 1) {
        await this.showAlert('Ошибка', 'Укажите Ваше имя');
        return;
      } else if (lastName.length < 1) {
        await this.showAlert('Ошибка', 'Укажите Вашу фамилию');
        return;
      } else if (password.length < 4) {
        await this.showAlert('Ошибка', 'Пароль должен быть не менее 4 символов');
        return;
      } else if (password !== confirmPassword) {
        await this.showAlert('Ошибка', 'Указанные пароли не совпадают');
        return;
      }

      const data = {
        login: email,
        email,
        firstName,
        lastName,
        password,
      };

      this.loading = true;
      try {
        if (this.isMobile) {
          const response = await CapacitorHttp.post({
            url: `${process.env.VUE_APP_API_URL}api/signup`,
            data,
            headers: { 'Content-Type': 'application/json' },
          });
          if (response.status < 200 || response.status >= 300) {
            await this.showAlert(
              'Ошибка',
              this.problemMessage(response.data, 'Не удалось зарегистрироваться. Попробуйте другой email')
            );
            return;
          }
        } else {
          await axios.post('api/signup', data, {
            headers: { 'Content-Type': 'application/json' },
          });
        }

        await this.loginAfterSignup(email, password);
      } catch (err: unknown) {
        if (isAxiosError(err) && err.response?.status === 400) {
          await this.showAlert('Ошибка', 'Попробуйте другой email');
        } else {
          await this.showAlert('Ошибка', 'Не удалось зарегистрироваться. Проверьте соединение и попробуйте снова');
        }
      } finally {
        this.loading = false;
      }
    },

    async loginAfterSignup(email: string, password: string): Promise<void> {
      const meta = await getClientDeviceMeta();
      const authPayload = {
        username: email,
        password,
        rememberMe: true,
        deviceName: meta.deviceName || undefined,
        platform: meta.platform || undefined,
        appVersion: meta.appVersion || undefined,
        appBuild: meta.appBuild || undefined,
      };

      let authResponse: { status: number; headers: Record<string, string>; data?: unknown };
      if (this.isMobile) {
        authResponse = await CapacitorHttp.post({
          url: `${process.env.VUE_APP_API_URL}api/authenticate`,
          data: authPayload,
          headers: { 'Content-Type': 'application/json' },
        });
        if (authResponse.status !== 200 && authResponse.status !== 1001) {
          await this.showAlert(
            'Сообщение',
            'Вы зарегистрированы. Войдите по паролю на экране входа.'
          );
          void router.replace({ path: '/login', query: { email } });
          return;
        }
      } else {
        const axiosRes = await axios.post('api/authenticate', authPayload);
        authResponse = {
          status: axiosRes.status,
          headers: axiosRes.headers as Record<string, string>,
          data: axiosRes.data,
        };
      }

      await this.accountService.persistJwtFromAuthResponse(authResponse);
      void registerAdminPushNotifications().then(() => flushPendingPushOpen());
      void flushPendingDeepLink();
      if (!this.globalContext.offlineMode) {
        this.accountService
          .hasAnyAuthorityAndCheckAuth('ROLE_MANAGER')
          .then((value) => {
            this.globalContext.isManager = value;
          });
      } else {
        this.globalContext.isManager = false;
      }
      void router.replace({ name: 'Showcase' });
    },

    async showAlert(alertTitle: string, alertMessage: string) {
      const alert = await alertController
        .create({
          header: alertTitle,
          message: alertMessage,
          buttons: ['OK'],
        });

      await alert.present();
      await alert.onDidDismiss();
    }
  }
});
</script>

<style scoped>
.signup-float-toolbar {
  --background: transparent;
  --border-width: 0;
  --padding-start: 0;
  --padding-end: 0;
  --padding-top: env(safe-area-inset-top);
  --min-height: 56px;
}
.signup-float-bar .liga-float-circle {
  appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: var(--liga-chrome-text);
  flex-shrink: 0;
}
.signup-float-bar .liga-float-circle ion-icon {
  font-size: 22px;
  color: var(--liga-chrome-text);
}
.signup-float-bar .liga-float-spacer {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
}

.signup-card {
  margin: 12px 12px 24px;
  box-shadow: none;
}

ion-input {
  --background: var(--liga-float-glass-bg-strong);
  --color: var(--liga-chrome-text);
  --placeholder-color: var(--liga-chrome-text-muted);
  border-radius: 25px;
  font-family: 'Montserrat';
  margin-bottom: 1.25em;
  --padding-start: 2em;
  --padding-end: 2em;
  border: 1px solid var(--liga-float-glass-border);
  box-shadow: var(--liga-float-glass-shadow);
}

.signup-button {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --color: #ffffff;
  width: 280px;
  height: 50px;
  font-family: 'Montserrat',
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-weight: bold;
  --border-radius: 14px;
}

.main-button-container {
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  margin-top: 0.5rem;
}
</style>
